import { APP_SETTINGS } from "../../shared/app_settings";
import { NoteEntry } from "../../shared/types";
import { markdownPlain } from "../../shared/markdown";
import { cloneTemplate, fillFields, flashToast } from "../dom";
import { iconSvg } from "../icons";
import { t } from "../i18n";
import { MAX_NOTE_PINS, isNotePinned, toggleNotePin } from "../note_pins";
import { firstLine, formatDate } from "../utils";

export function userNotes(notes: NoteEntry[]): NoteEntry[] {
  return notes.filter((note) => note.name !== APP_SETTINGS.scratchpadNoteName);
}

export function stickyColorFor(name: string): string {
  let hash = 0;
  for (const letter of name) hash = (hash * 31 + letter.charCodeAt(0)) >>> 0;
  return APP_SETTINGS.stickyColors[hash % APP_SETTINGS.stickyColors.length];
}

export function applyStickyColor(element: HTMLElement, name: string): void {
  APP_SETTINGS.stickyColors.forEach((color) => element.classList.remove(`sticky-${color}`));
  element.classList.add(`sticky-${stickyColorFor(name)}`);
}

// the pin in a note's corner, its own button so it works from the keyboard and never opens the note
function pinKey(note: NoteEntry): HTMLButtonElement {
  const key = document.createElement("button");
  key.type = "button";
  key.className = "sticky-pin";
  key.innerHTML = iconSvg("pin", 18);
  const paint = () => {
    const on = isNotePinned(note.name);
    key.setAttribute("aria-pressed", String(on));
    key.setAttribute("aria-label", t(on ? "unpin {name} from home" : "pin {name} to home", { name: note.name }));
    key.title = t(on ? "pinned to home, press to unpin" : "pin to home");
  };
  paint();
  key.addEventListener("click", (event) => {
    event.stopPropagation();
    if (!toggleNotePin(note.name)) flashToast(t("{count} pins is the limit, unpin one first", { count: MAX_NOTE_PINS }));
    paint();
  });
  key.addEventListener("keydown", (event) => event.stopPropagation());
  return key;
}

export function buildStickyNote(note: NoteEntry, onOpen: (note: NoteEntry) => void, pinnable = false): HTMLElement {
  const plain = markdownPlain(note.content);
  const preview = plain.split(" ").slice(0, 28).join(" ") || firstLine(note.content);
  const sticky = fillFields(cloneTemplate("template-note"), {
    name: note.name,
    preview,
    date: formatDate(note.updatedAt)
  });
  applyStickyColor(sticky, note.name);
  sticky.addEventListener("click", () => onOpen(note));
  if (pinnable) {
    sticky.classList.toggle("is-pinned", isNotePinned(note.name));
    sticky.append(pinKey(note));
  }
  return sticky;
}
